import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import axios from 'axios';
import { parseWorkflowFromAI, matchAppsToIntent } from '../../utils/aiWorkflowService';
import { trackAIGenerationSuccess, trackAIGenerationFailed } from '../../utils/posthog';

// Async thunk for generating workflow from AI
export const generateWorkflowFromAI = createAsyncThunk(
  'aiWorkflow/generateWorkflow',
  async ({ prompt, apps, currentWorkflow = null, workflowId = null }, { rejectWithValue }) => {
    try {
      // Call AI service directly (AWS Bedrock via API Gateway)
      const aiResponse = await parseWorkflowFromAI(prompt, apps, currentWorkflow, workflowId);
      
      return {
        suggestions: aiResponse.suggestions || [],
        explanation: aiResponse.explanation,
        confidence: aiResponse.confidence,
        fieldMappings: aiResponse.fieldMappings || {},
        source: aiResponse.source || 'ai',
        error: aiResponse.error,
      };
    } catch (error) {
      console.error('AI workflow generation error:', error);
      // Fallback to local matching if AI service fails
      const fallbackMatches = matchAppsToIntent(prompt, apps);
      return {
        suggestions: fallbackMatches || [],
        source: 'fallback',
        error: error.message,
      };
    }
  }
);

const initialState = {
  isGenerating: false,
  suggestions: [],
  error: null,
  history: [],
  explanation: null,
};

const aiWorkflowSlice = createSlice({
  name: 'aiWorkflow',
  initialState,
  reducers: {
    clearAISuggestions: (state) => {
      state.suggestions = [];
    },
    addToHistory: (state, action) => {
      state.history.push({
        prompt: action.payload.prompt,
        workflow: action.payload.workflow,
        timestamp: new Date().toISOString(),
      });
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateWorkflowFromAI.pending, (state) => {
        state.isGenerating = true;
        state.error = null;
      })
      .addCase(generateWorkflowFromAI.fulfilled, (state, action) => {
        state.isGenerating = false;
        state.suggestions = action.payload.suggestions || [];
        state.explanation = action.payload.explanation || null;
        if (action.payload.error) {
          state.error = action.payload.error;
        } else {
          state.error = null; // Clear error on success
        }
        // PostHog: AI success metrics
        const source = action.payload.source || 'ai';
        const suggestions = action.payload.suggestions || [];
        if (source === 'ai' && suggestions.length > 0) {
          try {
            const wf = suggestions[0];
            trackAIGenerationSuccess({
              node_count: wf?.nodes?.length ?? 0,
              edge_count: wf?.edges?.length ?? 0,
              has_field_mappings: !!(wf?.fieldMappings && Object.keys(wf.fieldMappings).length > 0),
            });
          } catch (_) {}
        } else if (source === 'fallback' || action.payload.error) {
          try {
            trackAIGenerationFailed(source === 'fallback' ? 'fallback' : 'error', {
              error_message: action.payload.error || undefined,
            });
          } catch (_) {}
        }
      })
      .addCase(generateWorkflowFromAI.rejected, (state, action) => {
        state.isGenerating = false;
        state.error = action.error.message || 'Failed to generate workflow';
        state.suggestions = [];
        try {
          trackAIGenerationFailed('rejected', {
            error_message: action.error?.message || String(action.error),
          });
        } catch (_) {}
      });
  },
});

export const { clearAISuggestions, addToHistory, clearError } = aiWorkflowSlice.actions;

// Selectors
export const selectAIGenerating = (state) => state.aiWorkflow.isGenerating;
export const selectAISuggestions = (state) => state.aiWorkflow.suggestions;
export const selectAIError = (state) => state.aiWorkflow.error;
export const selectAIHistory = (state) => state.aiWorkflow.history;
export const selectAIExplanation = (state) => state.aiWorkflow.explanation;

export default aiWorkflowSlice.reducer;
